import {
  Box,
  Link as BlankLink,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import {ArrowLeft, Edit} from '@mui/icons-material';
import clsx from 'clsx';
import moment from 'moment';
import React from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DoneIcon from '@mui/icons-material/Done';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {getCuroURL} from '../../utils/getCuroURL';
import Loading from '../Loading/Loading';
import theme, {themeColors} from '../../theme/Theme';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {
  BusinessUnitEnum,
  GlobalPermissionEnum,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {getReviewReason} from '../../features/notes/reviewReasonMap';
import {useStepsToInfo} from '../../pages/HandoverSummaryPage/useStepsToInfo';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import {KeyClientDataBanner} from '../KeyClientDataBanner/KeyClientDataBanner';
import {StepValidationTooltip} from './StepValidationTooltip';

const classes = {
  stepper: {
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  step: {
    backgroundColor: '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 40,
    height: 40,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },

  allDone: {
    backgroundColor: themeColors.purple,
  },

  active: {
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },

  activeFailed: {
    backgroundColor: themeColors.red,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },

  completed: {
    backgroundColor: themeColors.green,
  },

  loading: {
    backgroundColor: '#ccc',
  },

  error: {
    backgroundColor: themeColors.red,
  },

  container: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  alternativeLabel: {},
  line: {},
};

const Container = styled('div')(() => ({
  position: 'relative',
  zIndex: theme.zIndex.mobileStepper,
  marginBottom: 0,
}));

const ColorlibStepIcon: React.FC<{
  active: boolean;
  completed: boolean;
  error: boolean;
  icon: React.ReactNode;
  allDone: boolean;
}> = (props) => {
  const {active, completed, error} = props;

  return (
    <div
      style={{
        ...classes.step,
        ...(active && !error ? classes.active : {}),
        ...(active && error ? classes.activeFailed : {}),
        ...(completed && !active ? classes.completed : {}),
        ...(props.allDone ? classes.allDone : {}),
        ...(error ? classes.error : {}),
      }}
    >
      {props.icon}
    </div>
  );
};

export const HandoverStep: React.FC = () => {
  const navigate = useNavigate();
  const stepsToInfo = useStepsToInfo();
  const {
    stepValidation,
    allValid,
    currentStepIndex,
    loadingReview,
    fullValidationLoading,
    reviewValidation: review,
    reviewDetails,
    steps,
  } = useReviewContext();
  const {reviewDataIssues} = useReviewDataValidation();
  const {accountType} = useAccountContext();
  const isSFP = accountType === BusinessUnitEnum.SFP;

  if (loadingReview) {
    return (
      <Container>
        <Stepper>
          <Loading />
        </Stepper>
      </Container>
    );
  }
  if (!review) {
    return null;
  }
  const submitted = !review.isInJourney && review.curoId;
  return (
    <>
      <Container>
        <ShowIfDebugWrapper>
          Curo Id: {review.curoId || 'Not yet in curo'}
        </ShowIfDebugWrapper>
        <ReviewBreadcrumb />
        <Stepper
          sx={{...classes.stepper, background: 'none'}}
          nonLinear
          alternativeLabel
          orientation="horizontal"
          activeStep={currentStepIndex}
          connector={
            <ColorlibConnector
              classes={{
                alternativeLabel: clsx(classes.alternativeLabel),
                active: clsx(classes.active),
                completed: clsx(classes.completed),
                line: clsx(classes.line),
              }}
              sx={{
                top: '18px',
              }}
            />
          }
        >
          {steps.map((step, _index) => {
            const beforeCurrentStep = _index < currentStepIndex;
            const currentStep = _index === currentStepIndex;
            const stepLoading =
              step === 'summary' ? fullValidationLoading : stepValidation[step] === null;
            const stepIsValid = stepLoading
              ? null
              : step === 'summary'
              ? allValid
              : stepValidation[step];
            return (
              <Step
                completed={beforeCurrentStep && stepLoading === false}
                className={clsx({stepLoading})}
                onClick={(e) => {
                  if (process.env.NODE_ENV === 'development' || submitted || e.altKey) {
                    navigate({pathname: `../${step}`});
                  }
                }}
                aria-label={stepsToInfo[step].title}
                key={step}
              >
                <StepValidationTooltip step={step}>
                  <StepLabel
                    StepIconComponent={
                      ColorlibStepIcon as React.ElementType<StepIconProps>
                    }
                    StepIconProps={{icon: stepsToInfo[step].icon}}
                    sx={{
                      textTransform: 'capitalize',
                    }}
                    error={stepIsValid === false && (beforeCurrentStep || currentStep)}
                  >
                    <Typography
                      sx={{
                        top: -10,
                        position: 'relative',
                        fontWeight: 'bold',
                        textDecoration: currentStep ? 'underline' : 'none',
                      }}
                      color="primary"
                    >
                      {stepsToInfo[step].title}
                    </Typography>
                  </StepLabel>
                </StepValidationTooltip>
              </Step>
            );
          })}
        </Stepper>
      </Container>
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          mt: steps[currentStepIndex] === 'meeting' ? 10 : 0,
          mb: 2.5,
        }}
      >
        <KeyClientDataBanner issues={reviewDataIssues} isSFP={isSFP} />
      </Box>
    </>
  );
};

const ColorlibConnector = StepConnector;

const StatusWrapper = styled(Typography)`
  border-radius: 6px;
  padding-left: 6px;
  padding-right: 6px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 2px;

  svg {
    opacity: 0.6;
    height: 12px;
    width: 12px;
  }
`;
export const ReviewStatusText: React.FC<{
  review: Pick<ReviewDetailsFragment, 'statusOneview' | 'isSoftSubmitted'> | null;
}> = ({review}) => {
  if (!review) {
    return null;
  }

  if (review.isSoftSubmitted) {
    return (
      <StatusWrapper bgcolor={themeColors.lightYellow}>
        <Edit fontSize="small" />
        Soft Submitted
      </StatusWrapper>
    );
  }

  switch (review.statusOneview) {
    case ReviewStatusOneviewEnum.CLOSED:
      return (
        <StatusWrapper bgcolor={themeColors.lightGreen}>
          <DoneIcon fontSize="small" />
          Closed
        </StatusWrapper>
      );
    case ReviewStatusOneviewEnum.COMPLETED:
      return (
        <StatusWrapper bgcolor={themeColors.lightGreen}>
          <AutorenewIcon fontSize="small" />
          Submitted
        </StatusWrapper>
      );

    case ReviewStatusOneviewEnum.ERROR:
      return (
        <StatusWrapper bgcolor={themeColors.lightAlert}>
          <ReportProblemIcon fontSize="small" />
          Failed
        </StatusWrapper>
      );

    default:
      return (
        <StatusWrapper bgcolor={themeColors.lightBlue}>
          <Edit fontSize="small" />
          Open
        </StatusWrapper>
      );
  }
};

export const ReviewBreadcrumb: React.FC = () => {
  const {loadingReview, reviewValidation: review, reviewDetails} = useReviewContext();
  const {reviewId: _reviewId} = useParams();
  const {djangoUser} = useDjangoContext();
  const reviewId = _reviewId === 'undefined' ? '' : _reviewId;
  const {account, accountId, accountType} = useAccountContext();

  if (loadingReview || !review) {
    if (reviewId) {
      return null;
    }
  }

  const reviewReason = review ? getReviewReason(review)?.text : '';
  const reviewName = review?.name?.replace(/undefined/gi, '');
  const isReasonSameAsName = reviewReason === reviewName;

  return (
    <Typography
      style={{display: 'flex', marginBottom: 10, alignItems: 'center', gap: 5}}
      variant="body2"
    >
      <Link
        style={{
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
        }}
        to={{
          pathname: `/account/${account?.id || accountId}/${ACCOUNT_TAB.REVIEWS}`,
        }}
      >
        <ArrowLeft />
        {account?.name}
      </Link>
      {reviewName && review ? (
        <>
          |{' '}
          <Link
            style={{
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
            }}
            to={{
              pathname: `/account/${account?.id || accountId}/review-journey/${
                review.id
              }`,
            }}
          >
            {reviewName}
          </Link>
        </>
      ) : null}
      <ReviewStatusText review={review} />
      {!review?.dateSubmitted
        ? ''
        : ` on ${moment.utc(review.dateSubmitted).format('DD/MM/YYYY')} at ${moment
            .utc(review.dateSubmitted)
            .format('LT')} by ${review.submitter?.name}`}
      {reviewReason && !isReasonSameAsName ? ` | ${reviewReason}` : ''}
      {reviewDetails?.dateScheduled
        ? ` | Scheduled ${moment.utc(reviewDetails.dateScheduled).format('DD/MM/YYYY')} `
        : ''}
      {reviewDetails?.dateBooked
        ? ` | Meeting date ${moment.utc(reviewDetails.dateBooked).format('DD/MM/YYYY')} `
        : ''}{' '}
      <FeatureBoundary
        showErrorIfDenied={false}
        features={[GlobalPermissionEnum.can_access_curo_FE]}
      >
        | Account type: {accountType || 'Unknown'}
      </FeatureBoundary>
      {review?.curoId && (
        <>
          {' '}
          |{' '}
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_curo_FE]}
          >
            <BlankLink
              style={{color: 'inherit'}}
              target="_blank"
              href={getCuroURL(djangoUser, 'review', review?.curoId)}
            >
              View in CURO
            </BlankLink>
          </FeatureBoundary>
        </>
      )}
    </Typography>
  );
};
